/* eslint-disable react/prop-types */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages, MENU_LANGUAGE_CODES } from "../../../constants/menuLanguages";
import { assignItemsToChoice, removeItemFromChoice } from "../../../services/MenuService";
import { itemTitle } from "../Cms/MenuTree/menuTreeUtils";
import AddItem from "./AddItem";
import "../../../i18n";
import "../Cms/MenuTree/MenuTree.css";
import "./AddCategory.css";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
});
const asId = (value) => {
  if (value == null) return "";
  if (typeof value === "object" && value._id != null) return String(value._id);
  return String(value);
};

const EditChoice = ({ choiceId, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = choiceId || routeId;
  const embedded = Boolean(onClose);
  const languages = useMemo(() => getMenuLanguages(t), [t]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [choiceDetails, setChoiceDetails] = useState(() =>
    Object.fromEntries(MENU_LANGUAGE_CODES.map((code) => [code, { choiceTitle: "" }]))
  );
  const [openLangs, setOpenLangs] = useState({});
  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) => setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));

  const [minSelected, setMinSelected] = useState(0);
  const [maxSelected, setMaxSelected] = useState(1);
  const [obligatory, setObligatory] = useState(false);
  const [isFree, setIsFree] = useState(false);

  const [allItems, setAllItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [initialItemIds, setInitialItemIds] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [itemsOpen, setItemsOpen] = useState(false);
  const [showAddItemModal, setShowAddItemModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [choiceRes, itemsRes] = await Promise.all([
          axios.get(`${config.dev}/v2/choices/retrieve/${id}`, { headers: authHeaders() }),
          embedded
            ? Promise.resolve({ data: [] })
            : axios.get(`${config.dev}/v2/items/retrieve`, { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        const choice = choiceRes?.data;
        if (!choice?._id) {
          setNotFound(true);
          return;
        }

        const details = {};
        const nextOpen = {};
        MENU_LANGUAGE_CODES.forEach((code) => {
          const langData = choice.choiceDetails?.find((d) => d.language === code);
          details[code] = { choiceTitle: langData?.choiceTitle || "" };
          if (details[code].choiceTitle.trim()) nextOpen[code] = true;
        });
        setChoiceDetails(details);
        setOpenLangs(nextOpen);
        setMinSelected(Number(choice.minSelected) || 0);
        setMaxSelected(Number(choice.maxSelected) || 1);
        setObligatory(!!choice.obligatory);
        setIsFree(!!choice.isFree);

        if (!embedded) {
          const catalog = (itemsRes.data || []).filter((item) => !item.isArchived);
          const assigned = Array.isArray(choice.items)
            ? choice.items.filter((item) => item && item.isArchived !== true)
            : [];
          const assignedIds = assigned
            .map((item) => asId(item))
            .filter(Boolean);
          const merged = [...catalog];
          assigned.forEach((item) => {
            if (item && typeof item === "object" && item._id) {
              if (!merged.some((x) => asId(x) === asId(item))) merged.unshift(item);
            }
          });
          setAllItems(merged);
          setSelectedItemIds(assignedIds);
          setInitialItemIds(assignedIds);
          setItemsOpen(assignedIds.length > 0);
        }
      } catch (err) {
        console.error("EditChoice: load failed", err);
        toast.error(t("errorTitle"));
        setNotFound(true);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setItemsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, embedded, t]);

  const query = itemSearch.trim().toLowerCase();
  const visibleItems = allItems.filter(
    (item) => !query || itemTitle(item, "").toLowerCase().includes(query)
  );
  const itemsById = useMemo(
    () => Object.fromEntries(allItems.map((item) => [asId(item._id), item])),
    [allItems]
  );

  const toggleItem = (itemId) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId]
    );
  };

  const handleLangInputChange = (lang, field, value) => {
    setChoiceDetails((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;

    const choiceDetailsToSend = Object.entries(choiceDetails)
      .map(([lang, details]) => ({
        language: lang,
        choiceTitle: details.choiceTitle?.trim() || "",
      }))
      .filter((d) => d.choiceTitle);

    if (choiceDetailsToSend.length === 0) {
      swal(t("warningTitle"), t("wizardChoiceTitleRequired"), "warning");
      return;
    }
    if (maxSelected === 0) {
      swal(t("warningTitle"), t("warningMaxItemsToSelect"), "warning");
      return;
    }
    if (minSelected > maxSelected) {
      swal(t("warningTitle"), t("warningMinItemsToSelect"), "warning");
      return;
    }
    if (obligatory && minSelected === 0) {
      swal(t("warningTitle"), t("hotRequiresErrors"), "warning");
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${config.dev}/v2/choices/edit/${id}`, {
        choiceDetails: choiceDetailsToSend,
        minSelected,
        maxSelected,
        obligatory,
        isFree,
      });

      if (!embedded) {
        try {
          const toAdd = selectedItemIds.filter((itemId) => !initialItemIds.includes(itemId));
          const toRemove = initialItemIds.filter((itemId) => !selectedItemIds.includes(itemId));
          if (toAdd.length) await assignItemsToChoice(id, toAdd);
          for (const itemId of toRemove) {
            await removeItemFromChoice(id, itemId);
          }
        } catch (err) {
          console.error("EditChoice: assign items failed", err);
          toast.warn(t("choiceFormAttachFailed"));
        }
      }

      swal({
        title: t("successTitle"),
        text: t("choiceSuccess"),
        icon: "success",
        timer: 1000,
        buttons: false,
      });
      if (embedded) {
        onUpdated?.();
        onClose?.();
      } else {
        setTimeout(() => navigate(`/choice-details/${id}`), 1000);
      }
    } catch (err) {
      console.error("EditChoice: save failed", err);
      swal(t("errorTitle"), err?.response?.data?.message || String(err), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));

  const form = (
    <form
      className={`catform${embedded ? " catform--embed" : ""}`}
      onSubmit={handleSubmit}
      noValidate
    >
      {!embedded && (
        <div className="catform__top">
          <h1 className="catform__title">{t("editChoice")}</h1>
          <button
            type="button"
            className="btn catform__link"
            onClick={() => navigate(`/choice-details/${id}`)}
          >
            <i className="fas fa-arrow-left" aria-hidden /> {t("viewChoiceDetails")}
          </button>
        </div>
      )}

      <section className="catform__section">
        <h2 className="catform__heading">{t("catFormStepName")}</h2>
        <p className="catform__pills-label">{t("choiceFormChooseLanguage")}</p>
        <div className="catform__pills">
          {languages.map((lang) => {
            const filled = Boolean(choiceDetails[lang.code]?.choiceTitle?.trim());
            return (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                {filled && <i className="fas fa-check" aria-hidden />}
              </button>
            );
          })}
        </div>
        <div className="catform__lang-fields">
          {openLanguages.map((lang) => (
            <div className="catform__lang-panel" key={lang.code} dir={lang.code === "ar" ? "rtl" : undefined}>
              <div className="catform__field">
                <label className="catform__label" htmlFor={`edit-choice-title-${lang.code}`}>
                  <img src={lang.flag} alt="" />
                  {lang.label} — {t("title")}
                </label>
                <input
                  id={`edit-choice-title-${lang.code}`}
                  type="text"
                  className="catform__input"
                  value={choiceDetails[lang.code]?.choiceTitle || ""}
                  onChange={(e) => handleLangInputChange(lang.code, "choiceTitle", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("choiceFormRules")}</h2>
        <p className="catform__help">{t("choiceFormRulesHelp")}</p>
        <div className="catform__pair">
          <div className="catform__field">
            <label className="catform__label" htmlFor="edit-choice-min">
              {t("minSelection")}
            </label>
            <input
              id="edit-choice-min"
              type="number"
              min={0}
              className="catform__input"
              value={minSelected}
              onChange={(e) => setMinSelected(parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="catform__field">
            <label className="catform__label" htmlFor="edit-choice-max">
              {t("maxSelection")}
            </label>
            <input
              id="edit-choice-max"
              type="number"
              min={1}
              className="catform__input"
              value={maxSelected}
              onChange={(e) => setMaxSelected(parseInt(e.target.value, 10) || 0)}
            />
          </div>
        </div>
        <div className="catform__radios catform__radios--options">
          <label className={`catform__radio ${obligatory ? "is-on" : ""}`}>
            <input type="checkbox" checked={obligatory} onChange={() => setObligatory((v) => !v)} />
            {t("hotRequired")}
          </label>
          <label className={`catform__radio ${isFree ? "is-on" : ""}`}>
            <input type="checkbox" checked={isFree} onChange={() => setIsFree((v) => !v)} />
            {t("freeChoice")}
          </label>
        </div>
      </section>

      {!embedded && (
        <section className={`catform__section ${itemsOpen ? "is-open" : ""}`}>
          <button
            type="button"
            className="catform__fold"
            aria-expanded={itemsOpen}
            aria-controls="editchoice-items"
            onClick={() => setItemsOpen((open) => !open)}
          >
            <h2 className="catform__heading">
              {t("choiceFormItems")} <span className="catform__optional">{t("optional")}</span>
              {selectedItemIds.length > 0 && (
                <span className="catform__fold-count">{selectedItemIds.length}</span>
              )}
            </h2>
            <i
              className={`fas fa-chevron-down catform__fold-arrow ${itemsOpen ? "is-open" : ""}`}
              aria-hidden
            />
          </button>
          <p className="catform__help">{t("choiceFormItemsHelp")}</p>

          {itemsOpen && (
            <div id="editchoice-items" className="catform__fold-body">
              {selectedItemIds.length > 0 && (
                <div className="catform__chips">
                  {selectedItemIds.map((itemId) => (
                    <span key={itemId} className="catform__chip">
                      {itemTitle(itemsById[itemId], t("subFormUnknownProduct"))}
                      <button type="button" aria-label={t("remove")} onClick={() => toggleItem(itemId)}>
                        <i className="fas fa-times" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input
                type="search"
                className="catform__input"
                placeholder={`${t("searchItem")}…`}
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
              />
              <div className="catform__list">
                {itemsLoading ? (
                  <div className="catform__empty">{t("loading")}…</div>
                ) : allItems.length === 0 ? (
                  <div className="catform__empty">{t("itemFormEmpty")}</div>
                ) : visibleItems.length === 0 ? (
                  <div className="catform__empty">{t("subFormNoMatch")}</div>
                ) : (
                  visibleItems.map((item) => {
                    const itemId = asId(item._id);
                    const checked = selectedItemIds.includes(itemId);
                    return (
                      <label
                        key={itemId}
                        className={`catform__option ${checked ? "is-checked" : ""}`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleItem(itemId)} />
                        <span className="catform__option-name">{itemTitle(item)}</span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="catform__new">
                <button type="button" className="btn menu-picker__create" onClick={() => setShowAddItemModal(true)}>
                  <span className="menu-picker__create-plus" aria-hidden>
                    <i className="fas fa-plus" />
                  </span>
                  {t("addItem")}
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      <div className="catform__actions">
        <button
          type="button"
          className="btn catform__btn catform__btn--cancel"
          onClick={() => (embedded ? onClose?.() : navigate(`/choice-details/${id}`))}
          disabled={submitting}
        >
          {t("cancel")}
        </button>
        <button type="submit" className="btn catform__btn catform__btn--primary" disabled={submitting || loading}>
          {submitting ? t("loading") : t("saveChanges")}
        </button>
      </div>
    </form>
  );

  if (loading) {
    if (embedded) return <p className="catform__empty">{t("loading")}…</p>;
    return (
      <>
        <PageTitle activeMenu={t("editChoice")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("loading")}…</p>
        </div>
      </>
    );
  }

  if (notFound && !embedded) {
    return (
      <>
        <PageTitle activeMenu={t("editChoice")} motherMenu={t("kitchen")} />
        <div className="catform">
          <p className="catform__empty">{t("noTitle")}</p>
        </div>
      </>
    );
  }

  const body = (
    <>
      {form}
      {showAddItemModal && (
        <div className="modal-overlay" onClick={() => setShowAddItemModal(false)}>
          <div className="modal-container prod-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("addItem")}</h5>
              <button className="btn-close" onClick={() => setShowAddItemModal(false)} />
            </div>
            <div className="modal-body">
              <AddItem
                onSuccess={async () => {
                  try {
                    const itemsRes = await axios.get(`${config.dev}/v2/items/retrieve`, {
                      headers: authHeaders(),
                    });
                    setAllItems((itemsRes.data || []).filter((item) => !item.isArchived));
                  } catch (err) {
                    console.error(err);
                  }
                  setShowAddItemModal(false);
                }}
                onClose={() => setShowAddItemModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <>
      <PageTitle activeMenu={t("editChoice")} motherMenu={t("kitchen")} />
      {body}
    </>
  );
};

export default EditChoice;
